import { asArray, asNumber, pickOne } from '@/shared/domain/coerce';

/* ------------------------------------------------------------------ */
/* Doc 06 §4 / doc 12 §1 — Ecuación de cuadratura financiera:         */
/*   Bruto − Promos por ítem − Cupón = Neto                           */
/* Fuente única para exportación, conciliación, ticket sheet,         */
/* impresión, Mercado Pago y reembolsos (si divergen, el auditor ve   */
/* falsos desfasajes).                                                */
/* ------------------------------------------------------------------ */

/** Estructura mínima de un `order_item` para el cálculo del bruto. */
export type FinancialsItemLike = {
  price: number | null;
  quantity: number | null;
  order_sub_selection:
    { price: number | null; quantity: number | null } | { price: number | null; quantity: number | null }[] | null;
  /** Promo de producto aplicada al ítem (`order_product_promo`, que PostgREST entrega objeto o array). */
  order_product_promo?:
    | { discount: number | null; discount_type: string | null }
    | { discount: number | null; discount_type: string | null }[]
    | null;
};

/** Estructura mínima de un `order_discount` para el cálculo del descuento. */
export type FinancialsDiscountLike = {
  discount_code: string | null;
  discount: number | null;
  discount_type: string | null;
};

export interface OrderFinancials {
  /** Σ (precio de lista + Σ subselecciones) × cantidad. */
  subtotalBruto: number;
  /** Importe efectivo descontado por promos de producto (`order_product_promo`). */
  promoDiscountAmount: number;
  discountCode: string;
  /** Importe efectivo del cupón de carrito (percentage → % del subtotal promocionado; fixed → nominal). */
  discountAmount: number;
  /** Promo + cupón: siempre `bruto − neto` cuando hay datos para descomponerlo. */
  totalDiscountAmount: number;
  /** Neto real: `max(0, bruto − descuentos)`, salvo que `storedTotal` ya sea el neto histórico (`> 0` y `< bruto`). */
  totalNeto: number;
}

/** Trunca a 2 decimales sin redondear (misma regla que el carrito y el servidor del tótem). */
const trunc2 = (value: number) => Math.trunc(value * 100 + 1e-7) / 100;

/**
 * Descuento efectivo de una promo de producto sobre el precio de lista del ítem,
 * truncado antes de restar (misma regla que `calculatePromoUnitPrice` del tótem).
 */
export function calculatePromoDiscountForItem(
  itemPrice: number,
  promo: { discount: number | null; discount_type: string | null }
): number {
  const safePrice = trunc2(Math.max(0, itemPrice));
  const rawDiscount = asNumber(promo.discount) ?? 0;
  if (rawDiscount <= 0) return 0;

  const raw = promo.discount_type === 'percentage' ? (safePrice * rawDiscount) / 100 : rawDiscount;
  return Math.min(safePrice, trunc2(Math.max(0, raw)));
}

/** Bruto de la orden: `Σ (price + subselecciones) × quantity` por ítem (doc 06 §4). */
export function calculateSubtotalBruto(items: FinancialsItemLike[]): number {
  let subtotalBruto = 0;

  for (const item of items) {
    subtotalBruto += calculateItemUnitPrice(item) * (asNumber(item.quantity) ?? 1);
  }

  // Truncado final: las multiplicaciones de punto flotante dejan residuo (3000.0300000000002).
  return trunc2(subtotalBruto);
}

/** Precio unitario de un ítem: `price + Σ (subprecio × cantidad)`, sin multiplicar por la cantidad. */
export function calculateItemUnitPrice(item: FinancialsItemLike): number {
  const itemPrice = asNumber(item.price) ?? 0;

  let itemSubsTotal = 0;
  for (const sub of asArray(item.order_sub_selection)) {
    const subPrice = asNumber(sub.price) ?? 0;
    const subQty = asNumber(sub.quantity) ?? 1;
    itemSubsTotal += subPrice * subQty;
  }

  return itemPrice + itemSubsTotal;
}

/** Σ de los descuentos por promo de producto de todos los ítems (truncado por ítem × cantidad). */
export function calculatePromoDiscountTotal(items: FinancialsItemLike[]): number {
  let total = 0;

  for (const item of items) {
    const itemPrice = asNumber(item.price) ?? 0;
    const quantity = asNumber(item.quantity) ?? 1;

    let itemPromo = 0;
    for (const promo of asArray(item.order_product_promo)) {
      itemPromo += calculatePromoDiscountForItem(itemPrice, promo);
    }

    total += itemPromo * quantity;
  }

  return trunc2(total);
}

/**
 * Financiación completa de una orden (idéntico a la lógica histórica de `export-orders.ts`):
 * - promos de producto: descuento truncado por ítem (regla del tótem);
 * - cupón `percentage` → % sobre el subtotal **ya promocionado** (stacking decidido); `fixed` → nominal;
 * - neto: si `storedTotal` existe, `> 0` y `< bruto` ya es el neto persistido; si es el bruto
 *   histórico (bug doc 03) se recalcula `max(0, bruto − promos − cupón)`.
 */
export function calculateOrderFinancials(
  items: FinancialsItemLike[],
  discountRecord: FinancialsDiscountLike | FinancialsDiscountLike[] | undefined,
  storedTotal: number | null | undefined
): OrderFinancials {
  const subtotalBruto = calculateSubtotalBruto(items);

  // PostgREST devuelve la relación to-one como objeto, pero algunos selects la tipan/entregan
  // como array: normalizamos acá para que todos los consumidores se comporten igual.
  const discount = pickOne(discountRecord);
  const discountCode = discount?.discount_code || '';
  const stored = asNumber(storedTotal);

  // Sin ítems no se puede descomponer el bruto ni saber si el total persistido incluye descuentos:
  // se devuelve el total tal cual. Devolver 0 rompía la tab fiscal de conciliación.
  if (subtotalBruto <= 0) {
    return {
      subtotalBruto: 0,
      promoDiscountAmount: 0,
      discountCode,
      discountAmount: 0,
      totalDiscountAmount: 0,
      totalNeto: stored != null && stored > 0 ? stored : 0,
    };
  }

  const promoDiscountAmount = calculatePromoDiscountTotal(items);

  // Cupón sobre el subtotal promocionado (stacking decidido): la base es bruto − promos.
  const subtotalConPromo = Math.max(0, subtotalBruto - promoDiscountAmount);
  const rawCoupon = asNumber(discount?.discount) ?? 0;
  const couponAmount = discount
    ? discount.discount_type === 'percentage'
      ? Math.min(subtotalConPromo, trunc2((subtotalConPromo * rawCoupon) / 100))
      : Math.min(subtotalConPromo, trunc2(rawCoupon))
    : 0;

  const calculatedNet = Math.max(0, subtotalConPromo - couponAmount);
  const storedIsNet = stored != null && stored > 0 && stored < subtotalBruto;
  const totalNeto = storedIsNet ? Number(stored) : calculatedNet;

  // Si el total persistido ya es neto, los descuentos efectivos son `bruto − total`
  // (el redondeo de cada tramo puede diferir por centavos y descuadraría tabla/ticket).
  // El cupón conserva su cálculo y la diferencia se atribuye a las promos.
  let promoDiscountAmountFinal = promoDiscountAmount;
  let discountAmount = couponAmount;
  if (storedIsNet) {
    const totalEffective = trunc2(Math.max(0, subtotalBruto - totalNeto));
    discountAmount = Math.min(couponAmount, totalEffective);
    promoDiscountAmountFinal = trunc2(Math.max(0, totalEffective - discountAmount));
  }

  return {
    subtotalBruto,
    promoDiscountAmount: promoDiscountAmountFinal,
    discountCode,
    discountAmount,
    totalDiscountAmount: Math.max(0, subtotalBruto - totalNeto),
    totalNeto,
  };
}
